/**
 * Inventory Manager Dashboard - DSH-FE-003
 * AS9100D-Compliant Executive Inventory Command Center
 * 
 * Role Focus:
 * - Executive KPIs (Accuracy, Turns, Fill Rate)
 * - Financial metrics (Value, Aging)
 * - Operational health (Issues, Variances)
 * - Compliance overview
 * 
 * AS9100D Clauses:
 * - 9.1: Monitoring and Measurement - KPIs and analytics
 * - 8.5.1: Control of Production - Operational efficiency
 * - 8.5.2: Identification & Traceability - Inventory accuracy
 * - 8.7: Nonconforming Outputs - Quarantine status
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Package,
  Boxes,
  Shield,
  Target,
  Activity,
  RefreshCw,
  Loader2,
  ChevronRight,
  AlertCircle,
  Calendar,
  Warehouse,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Bell,
  FileWarning,
  ClipboardList,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import { useInventoryWebSocket } from '../hooks/useInventoryWebSocket';
import { DashboardSelector } from '../hooks/useInventoryDashboard';
import { PartChangeNotificationPanel } from '../components/PartChangeNotificationPanel';

/**
 * KPI Card Component - Executive metrics with targets
 */
const KPICard = ({ title, value, target, unit, trend, description, onClick, testId }) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <ArrowUpRight className="w-4 h-4 text-green-600" />;
    if (trend === 'down') return <ArrowDownRight className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };
  
  const getStatusColor = () => {
    const numValue = parseFloat(value);
    const numTarget = parseFloat(target);
    
    if (unit === '%') {
      if (numValue >= numTarget) return 'text-green-600';
      if (numValue >= numTarget * 0.95) return 'text-amber-600';
      return 'text-red-600';
    }
    if (unit === 'x') {
      if (numValue >= numTarget * 0.75) return 'text-green-600';
      return 'text-amber-600';
    }
    return 'text-[#101957]';
  };
  
  const progressPercent = Math.min((parseFloat(value) / parseFloat(target)) * 100, 100);
  
  return (
    <Card 
      className="p-5 hover:shadow-md transition-shadow cursor-pointer group bg-white"
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`text-3xl font-bold ${getStatusColor()}`}>
              {value}{unit}
            </span>
            {getTrendIcon()}
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs text-gray-400">Target</span>
          <p className="text-sm font-semibold text-gray-600">{target}{unit}</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full rounded-full transition-all ${
            progressPercent >= 100 ? 'bg-green-500' : 
            progressPercent >= 80 ? 'bg-amber-500' : 
            'bg-red-500'
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      <p className="text-xs text-gray-400">{description}</p>
    </Card>
  );
};

/**
 * Summary Stat Card - Compact metric display
 */
const SummaryStat = ({ label, value, icon: Icon, color = 'blue', subvalue, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    slate: 'bg-slate-50 text-slate-600'
  };
  
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100 hover:border-gray-200 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xl font-bold text-[#101957]">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {subvalue && <p className="text-xs text-gray-400 mt-0.5">{subvalue}</p>}
      </div>
    </div>
  );
};

/**
 * Alert Item Component
 */
const AlertItem = ({ alert }) => {
  const severityConfig = {
    CRITICAL: { bg: 'bg-red-50 border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' },
    HIGH: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
    MEDIUM: { bg: 'bg-yellow-50 border-yellow-200', icon: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-700' },
    LOW: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' }
  };
  
  const config = severityConfig[alert.severity] || severityConfig.MEDIUM;
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${config.bg}`}>
      <AlertTriangle className={`w-5 h-5 mt-0.5 ${config.icon} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className={`text-xs ${config.badge}`}>{alert.severity}</Badge>
        </div>
        <p className="text-sm text-gray-700 font-medium">{alert.message}</p>
        <p className="text-xs text-gray-500 mt-0.5">{alert.action}</p>
      </div>
    </div>
  );
};

/**
 * Approval Item Component
 */
const ApprovalItem = ({ approval, onApprove }) => {
  const priorityConfig = {
    CRITICAL: 'border-red-300 bg-red-50',
    HIGH: 'border-amber-300 bg-amber-50',
    MEDIUM: 'border-blue-200 bg-white'
  };
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${priorityConfig[approval.priority] || priorityConfig.MEDIUM}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700">{approval.title}</p>
        <p className="text-xs text-gray-500">Ref: {approval.reference}</p>
      </div>
      <Badge variant="outline" className="text-xs">
        {approval.type.replace(/_/g, ' ')}
      </Badge>
    </div>
  );
};

/**
 * Aging Chart Component (Bar visualization)
 */
const AgingChart = ({ data }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <div className="text-center py-4 text-gray-400 text-sm">
        No inventory data
      </div>
    );
  }
  
  const categories = [
    { key: '0_30_days', label: '0-30d', color: 'bg-green-500' },
    { key: '31_60_days', label: '31-60d', color: 'bg-blue-500' },
    { key: '61_90_days', label: '61-90d', color: 'bg-amber-500' },
    { key: '91_180_days', label: '91-180d', color: 'bg-orange-500' },
    { key: '181_365_days', label: '181-365d', color: 'bg-red-400' },
    { key: 'over_365_days', label: '>365d', color: 'bg-red-600' }
  ];
  
  const maxValue = Math.max(...Object.values(data));
  
  return (
    <div className="space-y-2">
      {categories.map(cat => {
        const value = data[cat.key] || 0;
        const width = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        
        return (
          <div key={cat.key} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">{cat.label}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
              <div 
                className={`h-full ${cat.color} transition-all`}
                style={{ width: `${width}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 w-12 text-right">
              {value}
            </span>
            <span className="text-xs text-gray-400 w-10 text-right">
              {percent}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main Inventory Manager Dashboard Component
 */
const InventoryManagerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Handle dashboard switch
  const handleDashboardSwitch = (path) => {
    navigate(path);
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Load dashboard data
  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const response = await api.get('/api/v1/inventory/v2/dashboard/manager');
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading manager dashboard:', err);
      setError('Failed to load dashboard data');
      // Set empty state
      setDashboardData({
        kpi_cards: {
          inventory_accuracy: { value: 0, target: 99.5, unit: '%', trend: 'neutral', description: 'No data' },
          stock_turn_rate: { value: 0, target: 4.0, unit: 'x', trend: 'neutral', description: 'No data' },
          fill_rate: { value: 0, target: 98.0, unit: '%', trend: 'neutral', description: 'No data' },
          on_time_delivery: { value: 0, target: 98.0, unit: '%', trend: 'neutral', description: 'No data' }
        },
        stock_overview: { total_on_hand: 0, total_available: 0, total_allocated: 0, total_qc_hold: 0, total_lots: 0 },
        operational_summary: {},
        cycle_count_summary: {},
        aging_breakdown: {},
        expiry_breakdown: {},
        transaction_volume: {},
        approvals_needed: [],
        alerts: [],
        compliance: { clauses: [] },
        generated_at: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // WebSocket for real-time updates
  const { isConnected, eventCount } = useInventoryWebSocket({
    enabled: true,
    onRefresh: () => loadDashboard(true),
    showToasts: true
  });

  const handleRefresh = async () => {
    await loadDashboard();
    toast.success('Dashboard refreshed');
  };

  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#101957] mx-auto mb-4" />
          <p className="text-gray-500">Loading executive dashboard...</p>
        </div>
      </div>
    );
  }

  const kpiCards = dashboardData?.kpi_cards || {};
  const stockOverview = dashboardData?.stock_overview || {};
  const operationalSummary = dashboardData?.operational_summary || {};
  const cycleCountSummary = dashboardData?.cycle_count_summary || {};
  const agingBreakdown = dashboardData?.aging_breakdown || {};
  const expiryBreakdown = dashboardData?.expiry_breakdown || {};
  const transactionVolume = dashboardData?.transaction_volume || {};
  const approvalsNeeded = dashboardData?.approvals_needed || [];
  const alerts = dashboardData?.alerts || [];
  const compliance = dashboardData?.compliance || {};

  return (
    <div className="h-full overflow-auto bg-slate-50" data-testid="inventory-manager-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#101957] to-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#101957]">Manager Dashboard</h1>
                {/* Dashboard Selector */}
                <DashboardSelector 
                  onSelect={handleDashboardSwitch}
                  currentPath={location.pathname}
                />
              </div>
              <p className="text-sm text-gray-500">
                {getGreeting()}. Here is your inventory performance overview.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* AS9100D Badge */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    9.1 KPIs
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs font-semibold">AS9100D 9.1</p>
                  <p className="text-xs">Monitoring and Measurement</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* WebSocket Status */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge 
                    variant="outline" 
                    className={`gap-1 ${isConnected 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                    data-testid="websocket-status"
                  >
                    {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {isConnected ? 'Live' : 'Offline'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {isConnected 
                    ? `Real-time updates active (${eventCount} events)` 
                    : 'Real-time updates disconnected'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {lastRefresh && (
              <span className="text-xs text-gray-400">
                Updated {formatIST(lastRefresh)}
              </span>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Alerts Banner (if any critical/high alerts) */}
        {alerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'HIGH').length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-700">
                {alerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'HIGH').length} Critical/High Priority Alerts
              </span>
            </div>
            <div className="space-y-2">
              {alerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'HIGH').slice(0, 3).map((alert, idx) => (
                <p key={idx} className="text-sm text-red-600">
                  • {alert.message}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Executive KPIs */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-[#101957] mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Key Performance Indicators
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Inventory Accuracy"
              value={kpiCards.inventory_accuracy?.value || 0}
              target={kpiCards.inventory_accuracy?.target || 99.5}
              unit={kpiCards.inventory_accuracy?.unit || '%'}
              trend={kpiCards.inventory_accuracy?.trend || 'neutral'}
              description={kpiCards.inventory_accuracy?.description || 'Cycle count accuracy'}
              testId="kpi-inventory-accuracy"
            />
            <KPICard
              title="Stock Turn Rate"
              value={kpiCards.stock_turn_rate?.value || 0}
              target={kpiCards.stock_turn_rate?.target || 4.0}
              unit={kpiCards.stock_turn_rate?.unit || 'x'}
              trend={kpiCards.stock_turn_rate?.trend || 'neutral'}
              description={kpiCards.stock_turn_rate?.description || 'Annualized turns'}
              testId="kpi-stock-turn-rate"
            />
            <KPICard
              title="Fill Rate"
              value={kpiCards.fill_rate?.value || 0}
              target={kpiCards.fill_rate?.target || 98}
              unit={kpiCards.fill_rate?.unit || '%'}
              trend={kpiCards.fill_rate?.trend || 'neutral'}
              description={kpiCards.fill_rate?.description || 'Order fulfillment'}
              testId="kpi-fill-rate"
            />
            <KPICard
              title="On-Time Delivery"
              value={kpiCards.on_time_delivery?.value || 0}
              target={kpiCards.on_time_delivery?.target || 98}
              unit={kpiCards.on_time_delivery?.unit || '%'}
              trend={kpiCards.on_time_delivery?.trend || 'neutral'}
              description={kpiCards.on_time_delivery?.description || 'Delivery performance'}
              testId="kpi-on-time-delivery"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Stock & Operations */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stock Overview */}
            <Card className="p-5" data-testid="stock-overview-card">
              <h3 className="text-lg font-semibold text-[#101957] mb-4 flex items-center gap-2">
                <Boxes className="w-5 h-5" />
                Stock Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryStat
                  label="Total On-Hand"
                  value={stockOverview.total_on_hand?.toLocaleString() || '0'}
                  icon={Package}
                  color="blue"
                  onClick={() => navigate('/inventory/stock')}
                />
                <SummaryStat
                  label="Available"
                  value={stockOverview.total_available?.toLocaleString() || '0'}
                  icon={CheckCircle2}
                  color="green"
                  onClick={() => navigate('/inventory/lots?status=AVAILABLE')}
                />
                <SummaryStat
                  label="Allocated"
                  value={stockOverview.total_allocated?.toLocaleString() || '0'}
                  icon={Clock}
                  color="purple"
                  onClick={() => navigate('/inventory/lots?status=ALLOCATED')}
                />
                <SummaryStat
                  label="QC Hold"
                  value={stockOverview.total_qc_hold?.toLocaleString() || '0'}
                  icon={AlertCircle}
                  color={stockOverview.total_qc_hold > 0 ? 'amber' : 'slate'}
                  onClick={() => navigate('/inventory/lots?status=QC_HOLD')}
                />
              </div>
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">
                    <strong>{stockOverview.total_lots || 0}</strong> active lots
                  </span>
                  <span className="text-sm text-gray-500">
                    <strong>{stockOverview.total_locations || 0}</strong> locations
                  </span>
                  <span className="text-sm text-gray-500">
                    <strong>{stockOverview.location_utilization || 0}%</strong> utilization
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/inventory/stock')}>
                  View Details <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </Card>

            {/* Operational Summary */}
            <Card className="p-5" data-testid="operational-summary-card">
              <h3 className="text-lg font-semibold text-[#101957] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Operational Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryStat
                  label="Pending GRNs"
                  value={operationalSummary.pending_grns || 0}
                  icon={Package}
                  color={operationalSummary.pending_grns > 5 ? 'amber' : 'blue'}
                  onClick={() => navigate('/inventory/receiving')}
                />
                <SummaryStat
                  label="Open Issues"
                  value={operationalSummary.open_issues || 0}
                  icon={Boxes}
                  color={operationalSummary.open_issues > 10 ? 'amber' : 'blue'}
                  onClick={() => navigate('/inventory/issue-transfer')}
                />
                <SummaryStat
                  label="Pending Inspections"
                  value={operationalSummary.pending_inspections || 0}
                  icon={ClipboardList}
                  color={operationalSummary.pending_inspections > 3 ? 'amber' : 'blue'}
                  onClick={() => navigate('/inventory/qc-dashboard')}
                />
                <SummaryStat
                  label="Quarantine Items"
                  value={operationalSummary.quarantine_items || 0}
                  icon={AlertTriangle}
                  color={operationalSummary.quarantine_items > 0 ? 'red' : 'slate'}
                  subvalue={operationalSummary.counterfeit_suspects > 0 ? `${operationalSummary.counterfeit_suspects} suspect` : null}
                  onClick={() => navigate('/inventory/quarantine')}
                />
              </div>
              
              {/* Cycle Count Status */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Cycle Count Status</span>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/inventory/cycle-counts')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="flex items-center gap-6">
                  <div>
                    <span className="text-2xl font-bold text-[#101957]">{cycleCountSummary.completed_mtd || 0}</span>
                    <p className="text-xs text-gray-500">Completed (MTD)</p>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-amber-600">{cycleCountSummary.due_this_week || 0}</span>
                    <p className="text-xs text-gray-500">Due This Week</p>
                  </div>
                  <div>
                    <span className={`text-2xl font-bold ${cycleCountSummary.overdue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {cycleCountSummary.overdue || 0}
                    </span>
                    <p className="text-xs text-gray-500">Overdue</p>
                  </div>
                  <div>
                    <span className={`text-2xl font-bold ${cycleCountSummary.accuracy_qtd >= 99 ? 'text-green-600' : 'text-amber-600'}`}>
                      {cycleCountSummary.accuracy_qtd || 0}%
                    </span>
                    <p className="text-xs text-gray-500">Accuracy (QTD)</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Aging Analysis */}
            <Card className="p-5" data-testid="aging-analysis-card">
              <h3 className="text-lg font-semibold text-[#101957] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Inventory Aging Analysis
              </h3>
              <AgingChart data={agingBreakdown} />
              
              {/* Expiry Summary */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Expiry Risk</span>
                </div>
                <div className="flex items-center gap-4">
                  {expiryBreakdown.expired > 0 && (
                    <Badge variant="destructive" className="text-sm">
                      {expiryBreakdown.expired} Expired
                    </Badge>
                  )}
                  {expiryBreakdown.expiring_30_days > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-sm">
                      {expiryBreakdown.expiring_30_days} expiring in 30d
                    </Badge>
                  )}
                  {expiryBreakdown.expiring_60_days > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-sm">
                      {expiryBreakdown.expiring_60_days} expiring in 60d
                    </Badge>
                  )}
                  {expiryBreakdown.total_at_risk === 0 && (
                    <Badge className="bg-green-100 text-green-700 text-sm">
                      No expiry risk
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Approvals & Alerts */}
          <div className="space-y-6">
            {/* Pending Approvals */}
            <Card className="p-5" data-testid="approvals-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#101957] flex items-center gap-2">
                  <FileWarning className="w-5 h-5" />
                  Pending Approvals
                </h3>
                <Badge variant="outline">{approvalsNeeded.length}</Badge>
              </div>
              
              <div className="space-y-2">
                {approvalsNeeded.length > 0 ? (
                  approvalsNeeded.slice(0, 5).map((approval, idx) => (
                    <ApprovalItem key={idx} approval={approval} />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">No pending approvals</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Alerts */}
            <Card className="p-5" data-testid="alerts-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#101957] flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Alerts
                </h3>
                <Badge variant={alerts.length > 0 ? 'destructive' : 'outline'}>{alerts.length}</Badge>
              </div>
              
              <div className="space-y-2">
                {alerts.length > 0 ? (
                  alerts.map((alert, idx) => (
                    <AlertItem key={idx} alert={alert} />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">No active alerts</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Part Configuration Changes Panel (AS9100D 8.1.2) */}
            <PartChangeNotificationPanel 
              showComplianceInfo={true}
              defaultExpanded={false}
            />

            {/* Transaction Volume */}
            <Card className="p-5" data-testid="transaction-volume-card">
              <h3 className="text-lg font-semibold text-[#101957] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Transaction Volume
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#101957]">{transactionVolume.today || 0}</p>
                  <p className="text-xs text-gray-500">Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#101957]">{transactionVolume.this_week || 0}</p>
                  <p className="text-xs text-gray-500">This Week</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#101957]">{transactionVolume.this_month || 0}</p>
                  <p className="text-xs text-gray-500">This Month</p>
                </div>
              </div>
            </Card>

            {/* AS9100D Compliance Card */}
            <Card className="p-5 bg-gradient-to-br from-[#101957] to-indigo-800 text-white">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                AS9100D Compliance
              </h3>
              <div className="space-y-2 text-sm">
                {compliance.clauses?.map((clause, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-white/80">
                      {clause.number} - {clause.title}
                    </span>
                    <Badge 
                      className={clause.status === 'ACTIVE' 
                        ? 'bg-green-500/20 text-green-300' 
                        : 'bg-amber-500/20 text-amber-300'}
                    >
                      {clause.status}
                    </Badge>
                  </div>
                ))}
              </div>
              
              {compliance.attention_items > 0 && (
                <div className="mt-4 p-2 bg-white/10 rounded-lg">
                  <p className="flex items-center gap-2 text-amber-300 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    {compliance.attention_items} items need attention
                  </p>
                </div>
              )}
            </Card>

            {/* Last Updated */}
            {dashboardData?.generated_at && (
              <p className="text-xs text-center text-gray-400">
                Data as of: {formatIST(dashboardData.generated_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagerDashboard;
